import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Plus, X, Save, Calculator, FileDown } from "lucide-react";
import { toast } from "sonner";
import html2pdf from "html2pdf.js";
import { LOGO_BASE64 } from "@/lib/logo";
import TagBarcodePicker from "@/components/TagBarcodePicker";
import PdfPromptStep from "@/components/PdfPromptStep";

const NEW_WSP_MRP_BLOCK = `
<div style="display:flex;gap:16px;margin-top:14px;">
  <div style="flex:1;border:1px solid #E7E5E4;border-radius:6px;padding:10px 14px;">
    <div style="font-size:9px;color:#78716c;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;margin-bottom:8px;">New WSP</div>
    <div style="height:22px;border-bottom:1px solid #ccc;"></div>
  </div>
  <div style="flex:1;border:1px solid #E7E5E4;border-radius:6px;padding:10px 14px;">
    <div style="font-size:9px;color:#78716c;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;margin-bottom:8px;">New MRP</div>
    <div style="height:22px;border-bottom:1px solid #ccc;"></div>
  </div>
</div>`;

function getDesignBaseCost(d) {
  const rd = d.result_data || {};
  const fd = d.form_data || {};
  const landed = rd.landed_cost || 0;
  const trims = rd.trims_cost || parseFloat(fd.trims_cost) || 0;
  const whPct = rd.warehouse_pct || 0;
  // Strip trims and warehouse: base = (landed - warehouse) - trims
  // warehouse was applied on (cost + trims), so: landed = (cost+trims) * (1 + wh/100)
  // We want cost without trims and without warehouse
  // landed = final_cost + warehouse_cost, where final_cost = cost_per_dhoti + trims (for regular)
  // Actually: landed = final_cost * (1 + whPct/100) where final_cost includes trims
  // So final_cost = landed / (1 + whPct/100), and base = final_cost - trims
  if (whPct > 0) {
    const finalCost = landed / (1 + whPct / 100);
    return finalCost - trims;
  }
  return landed - trims;
}

function getBadge(d) {
  const mode = d.result_data?.calc_mode || d.form_data?.calc_mode || "regular";
  const badges = {
    fob: { label: "FOB", cls: "bg-blue-50 text-blue-700" },
    shirt: { label: "Shirt", cls: "bg-purple-50 text-purple-700" },
    tissue: { label: "Tissue", cls: "bg-teal-50 text-teal-700" },
    regular: { label: "Dhoti", cls: "bg-stone-100 text-stone-600" },
  };
  return badges[mode] || badges.regular;
}

export default function ComboCostPage() {
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [trimsCost, setTrimsCost] = useState("");
  const [result, setResult] = useState(null);
  const [settings, setSettings] = useState({ warehouse_pct: 5 });

  // WSP/MRP state
  const [wspMarkup, setWspMarkup] = useState("");
  const [wspMarkdown, setWspMarkdown] = useState("");
  const [wspPrice, setWspPrice] = useState("");
  const [mrpMarkup, setMrpMarkup] = useState("");
  const [mrpMarkdown, setMrpMarkdown] = useState("");
  const [mrpPrice, setMrpPrice] = useState("");
  const [revMrp, setRevMrp] = useState("");
  const [revMu, setRevMu] = useState("");
  const [revMd, setRevMd] = useState("");
  const [revWspResult, setRevWspResult] = useState("");

  // Save
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveDesc, setSaveDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadedDesignId, setLoadedDesignId] = useState(null);
  const [tagStepId, setTagStepId] = useState(null);
  const [pdfStep, setPdfStep] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/saved-designs"), api.get("/settings")]).then(([dRes, sRes]) => {
      setDesigns(dRes.data);
      const s = {};
      (sRes.data || []).forEach((x) => { if (x.key === "warehouse_expenses") s.warehouse_pct = x.value; });
      setSettings(s);
    }).catch((err) => toast.error(formatApiError(err))).finally(() => setLoading(false));
  }, []);

  // Load combo from URL
  useEffect(() => {
    const loadId = searchParams.get("load");
    const autoExport = searchParams.get("autoExport") === "1";
    if (loadId && !loading && designs.length > 0) {
      api.get(`/saved-designs/${loadId}`).then((res) => {
        const fd = res.data.form_data || {};
        const rd = res.data.result_data || {};
        if (fd.calc_mode === "combo" || rd.calc_mode === "combo") {
          const itemIds = (fd.items || rd.items || []).map((i) => i.id);
          const selectedDesigns = designs.filter((d) => itemIds.includes(d.id));
          setSelected(selectedDesigns);
          setTrimsCost(String(fd.trims_cost || rd.trims_cost || ""));
          setResult(rd);
          setSaveName(res.data.design_name || "");
          setSaveDesc(res.data.description || "");
          setLoadedDesignId(loadId);
          if (rd.wsp_price) setWspPrice(String(rd.wsp_price));
          if (rd.mrp_price) setMrpPrice(String(rd.mrp_price));
          toast.success(`Loaded combo: ${res.data.design_name}`);
        }
        setSearchParams({}, { replace: true });

        if (autoExport) {
          setTimeout(() => {
            const btn = document.querySelector('[data-testid="combo-export-pdf"]');
            if (btn) {
              btn.click();
              // Combo export builds a multi-page PDF — give it ~2.5s before navigating away.
              setTimeout(() => navigate("/designs"), 2500);
            } else {
              toast.error("Could not auto-export — please click Export PDF on the page.");
            }
          }, 600);
        }
      }).catch(() => {});
    }
  }, [searchParams, loading, designs, setSearchParams]);

  const addDesign = (d) => {
    if (selected.length >= 4) { toast.info("Maximum 4 designs allowed"); return; }
    if (selected.find((s) => s.id === d.id)) { toast.info("Already selected"); return; }
    setSelected([...selected, d]);
    setSearch("");
    setResult(null);
  };

  const removeDesign = (id) => {
    setSelected(selected.filter((s) => s.id !== id));
    setResult(null);
  };

  const calculate = () => {
    if (selected.length === 0) { toast.error("Select at least one design"); return; }
    const items = selected.map((d) => ({ id: d.id, name: d.design_name, mode: d.result_data?.calc_mode || "regular", base_cost: getDesignBaseCost(d) }));
    const subTotal = items.reduce((sum, i) => sum + i.base_cost, 0);
    const trims = parseFloat(trimsCost) || 0;
    const costBeforeWh = subTotal + trims;
    const whPct = settings.warehouse_pct || 5;
    const whCost = costBeforeWh * (whPct / 100);
    const landedCost = costBeforeWh + whCost;
    setResult({ items, sub_total: subTotal, trims_cost: trims, cost_before_wh: costBeforeWh, warehouse_pct: whPct, warehouse_cost: whCost, landed_cost: landedCost });
    // Preserve WSP/MRP — recompute margin % bands against new landed cost
    if (wspPrice && landedCost > 0) {
      const wsp = parseFloat(wspPrice);
      setWspMarkup((((wsp - landedCost) / landedCost) * 100).toFixed(2));
      setWspMarkdown(wsp > 0 ? (((wsp - landedCost) / wsp) * 100).toFixed(2) : "");
      if (mrpPrice) {
        const mrp = parseFloat(mrpPrice);
        setMrpMarkup(wsp > 0 ? (((mrp - wsp) / wsp) * 100).toFixed(2) : "");
        setMrpMarkdown(mrp > 0 ? (((mrp - wsp) / mrp) * 100).toFixed(2) : "");
      }
    } else if (!wspPrice) {
      setWspMarkup(""); setWspMarkdown("");
      setMrpMarkup(""); setMrpMarkdown("");
    }
    toast.success("Combo cost calculated!");
  };

  const resetCombo = () => {
    setSelected([]); setTrimsCost(""); setResult(null);
    setSaveName(""); setSaveDesc(""); setLoadedDesignId(null);
    setWspMarkup(""); setWspMarkdown(""); setWspPrice("");
    setMrpMarkup(""); setMrpMarkdown(""); setMrpPrice("");
    setRevMrp(""); setRevMu(""); setRevMd(""); setRevWspResult("");
    setSearchParams({}, { replace: true });
  };

  // After tagging a barcode, the backend updates trims/wsp/mrp + recomputes landed cost.
  // Pull the fresh values so the PDF export uses them.
  const refreshAfterTag = async (designId) => {
    if (!designId) return;
    try {
      const res = await api.get(`/saved-designs/${designId}`);
      const rd = res.data.result_data || {};
      const fd = res.data.form_data || {};
      const landed = Number(rd.landed_cost || 0);
      if (fd.trims_cost != null) setTrimsCost(String(fd.trims_cost));
      setResult((prev) => ({ ...(prev || {}), ...rd }));
      if (rd.wsp_price) {
        const w = Number(rd.wsp_price);
        setWspPrice(String(w));
        if (landed > 0) {
          setWspMarkup((((w - landed) / landed) * 100).toFixed(2));
          setWspMarkdown(w > 0 ? (((w - landed) / w) * 100).toFixed(2) : "");
        }
      }
      if (rd.mrp_price) {
        const m = Number(rd.mrp_price);
        setMrpPrice(String(m));
        const wspBase = Number(rd.wsp_price) || landed;
        if (wspBase > 0) {
          setMrpMarkup((((m - wspBase) / wspBase) * 100).toFixed(2));
          setMrpMarkdown(m > 0 ? (((m - wspBase) / m) * 100).toFixed(2) : "");
        }
      }
    } catch (err) { /* silent */ }
  };


  const saveCombo = async () => {
    if (!saveName.trim() || !result) return;
    setSaving(true);
    try {
      const payload = {
        design_name: saveName.trim(),
        description: saveDesc.trim(),
        form_data: { calc_mode: "combo", items: result.items.map((i) => ({ id: i.id, name: i.name })), trims_cost: result.trims_cost },
        result_data: { ...result, calc_mode: "combo", wsp_price: wspPrice ? parseFloat(wspPrice) : null, mrp_price: mrpPrice ? parseFloat(mrpPrice) : null },
      };
      if (loadedDesignId) {
        await api.put(`/saved-designs/${loadedDesignId}`, payload);
        toast.success("Combo design updated!");
        setPdfStep(true);
      } else {
        const res = await api.post("/saved-designs", payload);
        toast.success("Combo design saved!");
        setTagStepId(res.data.id);
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const exportComboPdf = async () => {
    if (!result || selected.length === 0) return;

    // Build individual cost sheets for each selected design
    const buildIndividualSheet = (d) => {
      const rd = d.result_data || {};
      const fd = d.form_data || {};
      const mode = rd.calc_mode || fd.calc_mode || "regular";
      const landed = rd.landed_cost || 0;
      const s = parseFloat(fd.size) || 1;

      let specsHtml = "";
      let costHtml = "";

      if (mode === "fob") {
        specsHtml = `<div style="font-size:10px;color:#57534E;">Product Cost: <b>${rd.product_cost || fd.product_cost}</b> | Trims: <b>${rd.trims_cost || fd.trims_cost}</b> | Border: <b>${fd.border_type || "—"}</b></div>`;
        costHtml = `
          <tr><td style="padding:4px 0;color:#57534E;">Product Cost</td><td style="text-align:right;font-family:monospace;">${(rd.product_cost || 0).toFixed?.(2) || rd.product_cost}</td></tr>
          <tr><td style="padding:4px 0;color:#57534E;">Trims</td><td style="text-align:right;font-family:monospace;">${(rd.trims_cost || 0).toFixed?.(2) || "0.00"}</td></tr>
          <tr><td style="padding:4px 0;color:#57534E;">Warehouse (${rd.warehouse_pct || 12}%)</td><td style="text-align:right;font-family:monospace;">${(rd.warehouse_cost || 0).toFixed?.(2) || "0.00"}</td></tr>
          <tr style="background:#C2410C;color:white;font-weight:700;"><td style="padding:6px 8px;">Landed Cost</td><td style="padding:6px 8px;text-align:right;font-family:monospace;">${landed.toFixed?.(2) || landed}</td></tr>`;
      } else if (mode === "shirt") {
        specsHtml = `<div style="font-size:11px;display:flex;gap:24px;color:#57534E;"><div>Fabric Rate: <b>${fd.fabric_rate}/mtr</b></div><div>Consumption: <b>${fd.consumption}m</b></div><div>CMT Charge: <b>${fd.cmt}</b></div></div>`;
        const fabricCost = (parseFloat(fd.fabric_rate) * parseFloat(fd.consumption)).toFixed(2);
        costHtml = `
          <tr><td style="padding:5px 0;color:#57534E;">Fabric (${fd.fabric_rate} × ${fd.consumption}m)</td><td style="text-align:right;font-family:monospace;">${fabricCost}</td></tr>
          <tr><td style="padding:5px 0;color:#57534E;">CMT Charge</td><td style="text-align:right;font-family:monospace;">${parseFloat(fd.cmt || 0).toFixed(2)}</td></tr>
          <tr><td colspan="2" style="border-bottom:1px solid #e7e5e4;padding:2px 0;"></td></tr>
          <tr><td style="padding:5px 0;font-weight:600;">Cost / Shirt</td><td style="text-align:right;font-family:monospace;font-weight:600;">${rd.cost_per_shirt?.toFixed(2) || (parseFloat(fabricCost) + parseFloat(fd.cmt || 0)).toFixed(2)}</td></tr>
          <tr><td style="padding:5px 0;color:#57534E;">Warehouse (${rd.warehouse_pct || 12}%)</td><td style="text-align:right;font-family:monospace;">${rd.warehouse_cost?.toFixed(2) || "0.00"}</td></tr>
          <tr><td colspan="2" style="border-bottom:1px solid #e7e5e4;padding:2px 0;"></td></tr>
          <tr style="background:#C2410C;color:white;font-weight:700;"><td style="padding:6px 8px;">Landed Cost</td><td style="padding:6px 8px;text-align:right;font-family:monospace;">${landed.toFixed?.(2) || landed}</td></tr>`;
      } else {
        // Regular & Tissue modes — full UI-matching layout (same as main Dhoti Costing Sheet)
        const r = rd;
        const f = fd;
        const isTissue = mode === "tissue";

        const wspP = r.wsp_price || "";
        const mrpP = r.mrp_price || "";
        const fmtPct = (v) => (v !== undefined && v !== null && !isNaN(v) ? Number(v).toFixed(2) : "—");
        const wspMU = wspP && landed ? fmtPct(((wspP - landed) / landed) * 100) : "";
        const wspMD = wspP && landed ? fmtPct(((wspP - landed) / wspP) * 100) : "";
        const mrpMU = mrpP && wspP ? fmtPct(((mrpP - wspP) / wspP) * 100) : (mrpP && landed ? fmtPct(((mrpP - landed) / landed) * 100) : "");
        const mrpMD = mrpP ? fmtPct(((mrpP - (wspP || landed)) / mrpP) * 100) : "";

        const weftRow = isTissue && r.weft2_cost !== undefined
          ? `<tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Weft 1 (${r.weft_count}/${r.weft_quality})</td><td style="text-align:right;font-family:monospace;">${r.weft_cost?.toFixed(2) || "—"}</td><td style="text-align:right;font-family:monospace;">${r.weft_cost ? (r.weft_cost*s).toFixed(2) : "—"}</td></tr>
             <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Weft 2 (${r.weft2_count}/${r.weft2_quality})</td><td style="text-align:right;font-family:monospace;">${r.weft2_cost?.toFixed(2) || "—"}</td><td style="text-align:right;font-family:monospace;">${r.weft2_cost ? (r.weft2_cost*s).toFixed(2) : "—"}</td></tr>`
          : `<tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Weft Cost</td><td style="text-align:right;font-family:monospace;">${r.weft_cost?.toFixed(2) || "—"}</td><td style="text-align:right;font-family:monospace;">${r.weft_cost ? (r.weft_cost*s).toFixed(2) : "—"}</td></tr>`;

        return `
        <div style="page-break-after:always;font-family:'Work Sans','IBM Plex Sans',sans-serif;max-width:700px;padding:0;font-size:11px;color:#1C1917;">
          <div style="text-align:center;margin-bottom:10px;padding:8px 0;">
            <img src="${LOGO_BASE64}" style="height:44px;margin:0 auto;" />
            <div style="font-size:11px;color:#78716c;margin-top:6px;letter-spacing:2px;text-transform:uppercase;font-weight:600;">${isTissue ? "Tissue" : "Dhoti"} Costing Sheet</div>
          </div>

          <div style="background:linear-gradient(135deg,#C2410C,#7C2D12);color:white;border-radius:10px;padding:16px 20px;margin-bottom:12px;">
            <div style="font-size:14px;font-weight:700;text-align:center;margin-bottom:8px;">${d.design_name}</div>
            <div style="display:flex;justify-content:space-around;text-align:center;">
              <div><div style="font-size:9px;opacity:0.6;">Landed Cost</div><div style="font-size:20px;font-weight:700;">${landed.toFixed?.(2) || landed}</div></div>
              <div><div style="font-size:9px;opacity:0.6;">WSP</div><div style="font-size:20px;font-weight:700;">${wspP || "—"}</div>${wspP ? `<div style="font-size:12px;opacity:0.7;margin-top:2px;">MU ${wspMU}% / MD ${wspMD}%</div>` : ""}</div>
              <div><div style="font-size:9px;opacity:0.6;">MRP</div><div style="font-size:20px;font-weight:700;">${mrpP || "—"}</div>${mrpP ? `<div style="font-size:12px;opacity:0.7;margin-top:2px;">MU ${mrpMU}% / MD ${mrpMD}%</div>` : ""}</div>
            </div>
          </div>

          <div style="background:#F7F6F2;border:1px solid #E7E5E4;border-radius:8px;padding:12px 16px;margin-bottom:12px;">
            <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:8px;">Primary Specifications</div>
            <div style="display:flex;gap:24px;flex-wrap:wrap;">
              <div style="display:flex;gap:24px;flex-wrap:wrap;">
                <div><span style="color:#78716c;">Fabric Width:</span> <b>${f.fabric_width || "—"}</b></div>
                <div><span style="color:#78716c;">Border Width:</span> <b>${f.border_width || "—"}</b></div>
                <div><span style="color:#78716c;">Loom Reed:</span> <b>${f.reed || "—"}</b></div>
                <div><span style="color:#78716c;">Loom Pick:</span> <b>${f.pick || "—"}</b></div>
                <div><span style="color:#78716c;">Fabric Reed:</span> <b>${f.reed ? parseFloat(f.reed)+4 : "—"}</b></div>
                <div><span style="color:#78716c;">Fabric Pick:</span> <b>${f.pick ? parseFloat(f.pick)-2 : "—"}</b></div>
                <div><span style="color:#78716c;">Pick Rate:</span> <b>${f.pick_rate || "—"}</b></div>
                <div><span style="color:#78716c;">Size:</span> <b>${f.size || "—"}m</b></div>
              </div>
              <div style="display:flex;gap:24px;flex-wrap:wrap;">
                <div><span style="color:#78716c;">Border:</span> <b>${r.border_type || f.border_type || "—"}</b></div>
                <div><span style="color:#78716c;">Bobbin:</span> <b>${f.bobbin_type || "—"}</b></div>
                <div><span style="color:#78716c;">Processing:</span> <b>${r.processing_type || "—"}</b></div>
              </div>
              <div style="display:flex;gap:24px;flex-wrap:wrap;">
                <div><span style="color:#78716c;">Warp:</span> <b>${r.warp_count}/${r.warp_quality}</b> <span style="color:#78716c;font-size:9px;">@ ${r.warp_price_per_unit ?? "—"} + ${r.warp_sizing_per_unit ?? "—"}</span></div>
                <div><span style="color:#78716c;">${isTissue ? "Weft 1" : "Weft"}:</span> <b>${r.weft_count}/${r.weft_quality}</b> <span style="color:#78716c;font-size:9px;">@ ${r.weft_price_per_unit ?? "—"}</span></div>
                ${isTissue && r.weft2_count ? `<div><span style="color:#78716c;">Weft 2:</span> <b>${r.weft2_count}/${r.weft2_quality}</b> <span style="color:#78716c;font-size:9px;">@ ${r.weft2_price_per_unit ?? "—"}</span></div>` : ""}
                <div><span style="color:#78716c;">Kuri:</span> <b>${r.kuri_count}/${r.kuri_quality}</b> <span style="color:#78716c;font-size:9px;">@ ${r.kuri_price_per_unit ?? "—"}</span></div>
              </div>
              <div style="display:flex;gap:24px;flex-wrap:wrap;">
                <div><span style="color:#78716c;">Reed Space:</span> <b>${r.reed_space ?? "—"}</b></div>
                <div><span style="color:#78716c;">Warp Ends:</span> <b>${r.warp_ends ?? "—"}</b></div>
                <div><span style="color:#78716c;">Warp Wt/m:</span> <b>${r.warp_weight_per_mtr?.toFixed(4) ?? "—"}</b></div>
                <div><span style="color:#78716c;">Weft Wt/m:</span> <b>${r.weft_weight_per_mtr?.toFixed(4) ?? "—"}</b></div>
              </div>
              ${(d.vendor_names || []).length ? `<div style="width:100%;border-top:1px dashed #E7E5E4;padding-top:6px;margin-top:2px;"><span style="color:#78716c;">Vendors:</span> <b>${(d.vendor_names || []).join(", ")}</b></div>` : ""}
            </div>
          </div>

          <div style="display:flex;gap:12px;">
            <div style="flex:1;background:white;border:1px solid #E7E5E4;border-radius:8px;padding:14px 16px;">
              <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:10px;">Cost Breakdown</div>
              <table style="width:100%;border-collapse:collapse;font-size:11px;">
                <tr style="border-bottom:1px solid #eee;font-size:9px;color:#78716c;font-weight:600;text-transform:uppercase;">
                  <td style="padding:4px 0;"></td><td style="text-align:right;padding:4px 0;">Per Mtr</td><td style="text-align:right;padding:4px 0;">Per Dhoti</td>
                </tr>
                <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Warp Raw Cost</td><td style="text-align:right;font-family:monospace;">${r.warp_raw_cost?.toFixed(2) || "—"}</td><td style="text-align:right;font-family:monospace;">${r.warp_raw_cost ? (r.warp_raw_cost*s).toFixed(2) : "—"}</td></tr>
                <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Warp Sizing</td><td style="text-align:right;font-family:monospace;">${r.warp_sizing_charge?.toFixed(2) || "—"}</td><td style="text-align:right;font-family:monospace;">${r.warp_sizing_charge ? (r.warp_sizing_charge*s).toFixed(2) : "—"}</td></tr>
                <tr style="border-bottom:1px solid #e7e5e4;font-weight:600;"><td style="padding:5px 0;">Warp Total</td><td style="text-align:right;font-family:monospace;">${r.warp_total_cost?.toFixed(2) || "—"}</td><td style="text-align:right;font-family:monospace;">${r.warp_total_cost ? (r.warp_total_cost*s).toFixed(2) : "—"}</td></tr>
                ${weftRow}
                ${r.has_bobbin1 ? `<tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Bobbin - Polyester</td><td style="text-align:right;font-family:monospace;">${r.bobbin1_cost?.toFixed(2)}</td><td style="text-align:right;font-family:monospace;">${(r.bobbin1_cost*s).toFixed(2)}</td></tr>` : ""}
                ${r.has_bobbin2 ? `<tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Bobbin - Jari</td><td style="text-align:right;font-family:monospace;">${r.bobbin2_cost?.toFixed(2)}</td><td style="text-align:right;font-family:monospace;">${(r.bobbin2_cost*s).toFixed(2)}</td></tr>` : ""}
                <tr style="border-bottom:1px solid #e7e5e4;font-weight:600;"><td style="padding:5px 0;">Total Bobbin</td><td style="text-align:right;font-family:monospace;">${r.total_bobbin_cost?.toFixed(2) || "—"}</td><td style="text-align:right;font-family:monospace;">${r.total_bobbin_cost ? (r.total_bobbin_cost*s).toFixed(2) : "—"}</td></tr>
                <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Kuri (${r.kuri_count}/${r.kuri_quality})</td><td style="text-align:right;font-family:monospace;">—</td><td style="text-align:right;font-family:monospace;">${r.kuri_cost?.toFixed(2) || "—"}</td></tr>
                <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Weaving Wages</td><td style="text-align:right;font-family:monospace;">${r.weaving_wages?.toFixed(2) || "—"}</td><td style="text-align:right;font-family:monospace;">${r.weaving_wages ? (r.weaving_wages*s).toFixed(2) : "—"}</td></tr>
                <tr style="border-bottom:2px solid #C2410C;font-weight:700;"><td style="padding:6px 0;">Cost</td><td style="text-align:right;font-family:monospace;">${r.cost_per_mtr?.toFixed(2) || "—"}</td><td style="text-align:right;font-family:monospace;">${r.cost_per_dhoti?.toFixed(2) || "—"}</td></tr>
                <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Weaver Profit (${r.weaver_profit_pct ?? 10}%)</td><td></td><td style="text-align:right;font-family:monospace;">${r.profit_amount?.toFixed(2) || "—"}</td></tr>
                <tr style="font-weight:700;font-size:12px;"><td style="padding:8px 0;color:#C2410C;">Greige Cost</td><td></td><td style="text-align:right;font-family:monospace;color:#C2410C;">${r.greige_cost_per_dhoti?.toFixed(2) || "—"}</td></tr>
              </table>
            </div>

            <div style="flex:1;display:flex;flex-direction:column;gap:12px;">
              <div style="background:white;border:1px solid #E7E5E4;border-radius:8px;padding:14px 16px;">
                <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:10px;">Post Production</div>
                <table style="width:100%;border-collapse:collapse;font-size:11px;">
                  <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Processing <span style="color:#a8a29e;font-size:10px;">(${r.processing_mtr_rate ?? "—"}/mtr x ${s})</span></td><td style="text-align:right;font-family:monospace;">${r.processing_cost?.toFixed(2) || "—"}</td></tr>
                  <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Folding</td><td style="text-align:right;font-family:monospace;">${r.folding_cost?.toFixed(2) || "—"}</td></tr>
                  <tr style="border-bottom:1px solid #e7e5e4;"><td style="padding:5px 0;color:#57534E;">Trims</td><td style="text-align:right;font-family:monospace;">${r.trims_cost?.toFixed(2) || "0.00"}</td></tr>
                  <tr style="border-bottom:2px solid #1C1917;font-weight:700;"><td style="padding:6px 0;">Final Cost</td><td style="text-align:right;font-family:monospace;">${r.final_cost_per_dhoti?.toFixed(2) || "—"}</td></tr>
                  <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Warehouse (${r.warehouse_pct ?? 12}%)</td><td style="text-align:right;font-family:monospace;">${r.warehouse_cost?.toFixed(2) || "—"}</td></tr>
                  <tr style="font-weight:700;font-size:13px;color:#C2410C;"><td style="padding:8px 0;">Landed Cost</td><td style="text-align:right;font-family:monospace;">${landed.toFixed?.(2) || landed}</td></tr>
                </table>
              </div>

              <div style="background:white;border:1px solid #E7E5E4;border-radius:8px;padding:14px 16px;">
                <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:10px;">Pricing</div>
                <table style="width:100%;border-collapse:collapse;font-size:11px;">
                  ${wspP ? `
                  <tr style="border-bottom:1px solid #f5f5f4;font-weight:600;"><td style="padding:5px 0;">WSP</td><td style="text-align:right;font-family:monospace;font-size:13px;">${wspP}</td></tr>
                  <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:4px 0;color:#78716c;font-size:10px;">WSP Markup / Markdown</td><td style="text-align:right;color:#78716c;font-size:10px;">${wspMU}% / ${wspMD}%</td></tr>
                  ` : ""}
                  ${mrpP ? `
                  <tr style="border-bottom:1px solid #f5f5f4;font-weight:600;"><td style="padding:5px 0;">MRP</td><td style="text-align:right;font-family:monospace;font-size:13px;">${mrpP}</td></tr>
                  <tr><td style="padding:4px 0;color:#78716c;font-size:10px;">MRP Markup / Markdown</td><td style="text-align:right;color:#78716c;font-size:10px;">${mrpMU}% / ${mrpMD}%</td></tr>
                  ` : ""}
                  ${!wspP && !mrpP ? `<tr><td style="padding:5px 0;color:#a8a29e;" colspan="2">No pricing set</td></tr>` : ""}
                </table>
              </div>
            </div>
          </div>

          <div style="text-align:center;font-size:8px;color:#a8a29e;margin-top:10px;">Generated on ${new Date().toLocaleDateString("en-IN", {day:"2-digit",month:"short",year:"numeric"})}</div>
          ${NEW_WSP_MRP_BLOCK}
        </div>`;
      }

      // FOB / Shirt fallback wrapper
      const wspMrp = rd.wsp_price ? `<tr><td style="padding:4px 0;font-weight:600;">WSP</td><td style="text-align:right;font-family:monospace;font-weight:600;">${rd.wsp_price}</td></tr>` : "";
      const mrpRow = rd.mrp_price ? `<tr><td style="padding:4px 0;font-weight:600;">MRP</td><td style="text-align:right;font-family:monospace;font-weight:600;">${rd.mrp_price}</td></tr>` : "";

      return `
        <div style="page-break-after:always;font-family:'Work Sans',sans-serif;max-width:600px;margin:0 auto;padding:20px 0;">
          <div style="text-align:center;margin-bottom:12px;"><img src="${LOGO_BASE64}" style="height:40px;" /><div style="font-size:11px;color:#78716c;margin-top:4px;text-transform:uppercase;letter-spacing:2px;font-weight:600;">Cost Sheet</div></div>
          <div style="text-align:center;font-size:15px;font-weight:700;margin-bottom:8px;">${d.design_name} <span style="font-size:10px;color:#78716c;font-weight:400;">(${mode.toUpperCase()})</span></div>
          <div style="background:#F7F6F2;border:1px solid #E7E5E4;border-radius:6px;padding:8px 12px;margin-bottom:10px;">${specsHtml}${(d.vendor_names || []).length ? `<div style="border-top:1px dashed #E7E5E4;padding-top:5px;margin-top:5px;font-size:10px;"><span style="color:#78716c;">Vendors:</span> <b>${(d.vendor_names || []).join(", ")}</b></div>` : ""}</div>
          <table style="width:100%;border-collapse:collapse;font-size:11px;">${costHtml}${wspMrp}${mrpRow}</table>
          ${NEW_WSP_MRP_BLOCK}
        </div>`;
    };

    // Compute MU/MD against landed cost for the combo summary
    const cLanded = result.landed_cost || 0;
    const wspN = parseFloat(wspPrice) || 0;
    const mrpN = parseFloat(mrpPrice) || 0;
    const cWspMU = wspN > 0 && cLanded > 0 ? (((wspN - cLanded) / cLanded) * 100).toFixed(2) : "";
    const cWspMD = wspN > 0 && cLanded > 0 ? (((wspN - cLanded) / wspN) * 100).toFixed(2) : "";
    const cMrpMU = mrpN > 0 && wspN > 0 ? (((mrpN - wspN) / wspN) * 100).toFixed(2) : (mrpN > 0 && cLanded > 0 ? (((mrpN - cLanded) / cLanded) * 100).toFixed(2) : "");
    const cMrpMD = mrpN > 0 ? (((mrpN - (wspN || cLanded)) / mrpN) * 100).toFixed(2) : "";

    // Build combo summary sheet
    const comboSheet = `
      <div style="font-family:'Work Sans',sans-serif;max-width:600px;margin:0 auto;padding:20px 0;">
        <div style="text-align:center;margin-bottom:12px;"><img src="${LOGO_BASE64}" style="height:40px;" /><div style="font-size:11px;color:#78716c;margin-top:4px;text-transform:uppercase;letter-spacing:2px;font-weight:600;">Combo Cost Sheet</div></div>
        <div style="text-align:center;font-size:15px;font-weight:700;margin-bottom:12px;">${saveName || "Combo"}</div>
        <table style="width:100%;border-collapse:collapse;font-size:12px;">
          ${result.items.map((item) => {
            const sd = selected.find((s) => s.id === item.id);
            const v = (sd?.vendor_names || []).join(", ");
            return `<tr><td style="padding:5px 0;color:#57534E;">${item.name} <span style="font-size:9px;color:#a8a29e;">(${item.mode})</span>${v ? `<div style="font-size:9px;color:#a8a29e;margin-top:1px;">Vendors: ${v}</div>` : ""}</td><td style="text-align:right;font-family:monospace;vertical-align:top;">${item.base_cost.toFixed(2)}</td></tr>`;
          }).join("")}
          <tr><td colspan="2" style="border-bottom:1px solid #e7e5e4;padding:3px 0;"></td></tr>
          <tr><td style="padding:5px 0;font-weight:600;">Sub Total</td><td style="text-align:right;font-family:monospace;font-weight:600;">${result.sub_total.toFixed(2)}</td></tr>
          <tr><td style="padding:5px 0;color:#57534E;">Combo Trims</td><td style="text-align:right;font-family:monospace;">${result.trims_cost.toFixed(2)}</td></tr>
          <tr><td colspan="2" style="border-bottom:1px solid #e7e5e4;padding:3px 0;"></td></tr>
          <tr><td style="padding:5px 0;font-weight:600;">Cost before WH</td><td style="text-align:right;font-family:monospace;font-weight:600;">${result.cost_before_wh.toFixed(2)}</td></tr>
          <tr><td style="padding:5px 0;color:#57534E;">Warehouse (${result.warehouse_pct}%)</td><td style="text-align:right;font-family:monospace;">${result.warehouse_cost.toFixed(2)}</td></tr>
          <tr><td colspan="2" style="border-bottom:1px solid #e7e5e4;padding:3px 0;"></td></tr>
          <tr style="background:#C2410C;color:white;font-weight:700;"><td style="padding:8px;">Landed Cost</td><td style="padding:8px;text-align:right;font-family:monospace;">${result.landed_cost.toFixed(2)}</td></tr>
          ${wspPrice ? `<tr><td style="padding:5px 0;font-weight:600;">WSP</td><td style="text-align:right;font-family:monospace;font-weight:600;">${wspPrice}</td></tr>` : ""}
          ${wspPrice && cWspMU ? `<tr><td style="padding:4px 0;color:#78716c;font-size:10px;">WSP Markup / Markdown</td><td style="text-align:right;color:#78716c;font-size:10px;">${cWspMU}% / ${cWspMD}%</td></tr>` : ""}
          ${mrpPrice ? `<tr><td style="padding:5px 0;font-weight:600;">MRP</td><td style="text-align:right;font-family:monospace;font-weight:600;">${mrpPrice}</td></tr>` : ""}
          ${mrpPrice && cMrpMU ? `<tr><td style="padding:4px 0;color:#78716c;font-size:10px;">MRP Markup / Markdown</td><td style="text-align:right;color:#78716c;font-size:10px;">${cMrpMU}% / ${cMrpMD}%</td></tr>` : ""}
        </table>
        ${NEW_WSP_MRP_BLOCK}
        <div style="text-align:center;font-size:9px;color:#a8a29e;margin-top:16px;">Generated on ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
      </div>`;

    // Combine: individual sheets + combo sheet
    const allPages = selected.map((d) => buildIndividualSheet(d)).join("") + comboSheet;

    const container = document.createElement("div");
    container.innerHTML = allPages;
    document.body.appendChild(container);
    html2pdf().set({
      margin: [8, 8, 8, 8],
      filename: `Combo_${saveName || "Design"}_${new Date().toISOString().slice(0, 10)}.pdf`,
      image: { type: "jpeg", quality: 0.92 },
      html2canvas: { scale: 1.5, useCORS: true, logging: false },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["css"] },
    }).from(container).save().then(() => document.body.removeChild(container));
    toast.success("Combo PDF exported!");
  };



  const filtered = designs.filter((d) => d.design_name.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-stone-400" /></div>;

  return (
    <div className="space-y-6" data-testid="combo-cost-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">Combo Cost Calculator</h1>
        <p className="text-sm text-stone-500 mt-1">Bundle up to 4 products, apply combo trims and warehouse to get combined landed cost.</p>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Design Selection */}
        <div className="bg-white border border-stone-200 rounded-lg p-5 space-y-4">
          <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Select Designs (max 4)</h3>

          {selected.length > 0 && (
            <div className="space-y-2">
              {selected.map((d, idx) => {
                const badge = getBadge(d);
                const base = getDesignBaseCost(d);
                return (
                  <div key={d.id} className="flex items-center gap-3 bg-stone-50 rounded-lg px-3 py-2" data-testid={`combo-item-${idx}`}>
                    <span className="text-xs text-stone-400 font-mono w-5">{idx + 1}.</span>
                    <span className="text-sm font-medium flex-1 truncate">{d.design_name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${badge.cls}`}>{badge.label}</span>
                    <span className="text-sm font-mono font-semibold text-stone-700 w-20 text-right">{base.toFixed(2)}</span>
                    <button className="text-stone-400 hover:text-red-500" onClick={() => removeDesign(d.id)}>
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {selected.length < 4 && (
            <div className="relative">
              <Input
                data-testid="combo-design-search"
                placeholder="Search designs to add..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-10"
              />
              {search && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {filtered.length === 0 ? (
                    <div className="px-3 py-3 text-sm text-stone-400 text-center">No designs found</div>
                  ) : filtered.slice(0, 20).map((d) => {
                    const badge = getBadge(d);
                    return (
                      <div key={d.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-orange-50" onClick={() => addDesign(d)}>
                        <Plus className="h-3.5 w-3.5 text-stone-400" />
                        <span className="flex-1 truncate">{d.design_name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${badge.cls}`}>{badge.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Trims + Calculate */}
        <div className="bg-white border border-stone-200 rounded-lg p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Combo Trims / Packing Cost</Label>
              <Input data-testid="combo-trims-input" type="number" step="0.01" placeholder="e.g. 12" value={trimsCost} onChange={(e) => setTrimsCost(e.target.value)} />
            </div>
            <div className="flex items-end">
              <Button data-testid="combo-calculate-button" className="w-full bg-[#C2410C] hover:bg-[#9A3412] text-white h-10" onClick={calculate} disabled={selected.length === 0}>
                <Calculator className="h-4 w-4 mr-2" /> Calculate Combo
              </Button>
            </div>
          </div>
        </div>

        {/* Results */}
        {result && (
          <>
            <div className="bg-white border border-stone-200 rounded-lg p-5">
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-3">Cost Breakdown</h3>
              <div className="space-y-2 text-sm">
                {result.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between">
                    <span className="text-stone-500">{item.name} <span className="text-[10px] text-stone-400">({item.mode})</span></span>
                    <span className="font-mono">{item.base_cost.toFixed(2)}</span>
                  </div>
                ))}
                <Separator className="my-2" />
                <div className="flex justify-between font-semibold"><span>Sub Total</span><span className="font-mono">{result.sub_total.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-stone-500">Combo Trims</span><span className="font-mono">{result.trims_cost.toFixed(2)}</span></div>
                <Separator className="my-2" />
                <div className="flex justify-between font-semibold"><span>Cost before WH</span><span className="font-mono">{result.cost_before_wh.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-stone-500">Warehouse ({result.warehouse_pct}%)</span><span className="font-mono">{result.warehouse_cost.toFixed(2)}</span></div>
                <Separator className="my-2" />
                <div className="flex justify-between text-base font-bold text-[#C2410C]"><span>Landed Cost</span><span className="font-mono">{result.landed_cost.toFixed(2)}</span></div>
              </div>
            </div>

            {/* WSP & MRP */}
            <div className="bg-white border border-stone-200 rounded-lg p-5">
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-4">WSP & MRP Pricing</h3>
              <div className="space-y-5">
                <div>
                  <Label className="text-sm font-medium text-stone-700 mb-2 block">WSP (from Landed Cost: {result.landed_cost.toFixed(2)})</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <div><span className="text-xs text-stone-400">Markup %</span>
                      <Input data-testid="combo-wsp-markup" type="number" step="0.01" value={wspMarkup} onChange={(e) => { const v=e.target.value; setWspMarkup(v); setWspMarkdown(""); if(v&&result){const p=Math.round(result.landed_cost*(1+parseFloat(v)/100));setWspPrice(p);setWspMarkdown((((p-result.landed_cost)/p)*100).toFixed(2));setMrpMarkup("");setMrpMarkdown("");setMrpPrice("");}else{setWspPrice("");}}} className="h-9 mt-1" />
                    </div>
                    <div><span className="text-xs text-stone-400">Markdown %</span>
                      <Input type="number" step="0.01" value={wspMarkdown} onChange={(e) => { const v=e.target.value; setWspMarkdown(v); setWspMarkup(""); if(v&&result){const md=parseFloat(v);if(md<100){const p=Math.round(result.landed_cost/(1-md/100));setWspPrice(p);setWspMarkup((((p-result.landed_cost)/result.landed_cost)*100).toFixed(2));setMrpMarkup("");setMrpMarkdown("");setMrpPrice("");}}else{setWspPrice("");}}} className="h-9 mt-1" />
                    </div>
                    <div><span className="text-xs text-stone-400">WSP Price</span>
                      <Input data-testid="combo-wsp-price" type="number" step="1" value={wspPrice} onChange={(e) => { const p=e.target.value; setWspPrice(p); if(p&&result){const pr=parseFloat(p);if(pr>0){setWspMarkup((((pr-result.landed_cost)/result.landed_cost)*100).toFixed(2));setWspMarkdown((((pr-result.landed_cost)/pr)*100).toFixed(2));setMrpMarkup("");setMrpMarkdown("");setMrpPrice("");}}else{setWspMarkup("");setWspMarkdown("");}}} className="h-9 mt-1 font-semibold" />
                    </div>
                  </div>
                </div>
                <Separator />
                <div>
                  <Label className="text-sm font-medium text-stone-700 mb-2 block">MRP (from WSP: {wspPrice || "—"})</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <div><span className="text-xs text-stone-400">Markup %</span>
                      <Input data-testid="combo-mrp-markup" type="number" step="0.01" value={mrpMarkup} onChange={(e) => { const v=e.target.value; setMrpMarkup(v); setMrpMarkdown(""); const wsp=parseFloat(wspPrice); if(v&&wsp>0){const p=Math.round(wsp*(1+parseFloat(v)/100)/5)*5;setMrpPrice(p);setMrpMarkdown((((p-wsp)/p)*100).toFixed(2));}else{setMrpPrice("");}}} className="h-9 mt-1" disabled={!wspPrice} />
                    </div>
                    <div><span className="text-xs text-stone-400">Markdown %</span>
                      <Input type="number" step="0.01" value={mrpMarkdown} onChange={(e) => { const v=e.target.value; setMrpMarkdown(v); setMrpMarkup(""); const wsp=parseFloat(wspPrice); if(v&&wsp>0){const md=parseFloat(v);if(md<100){const p=Math.round(wsp/(1-md/100)/5)*5;setMrpPrice(p);setMrpMarkup((((p-wsp)/wsp)*100).toFixed(2));}}else{setMrpPrice("");}}} className="h-9 mt-1" disabled={!wspPrice} />
                    </div>
                    <div><span className="text-xs text-stone-400">MRP Price</span>
                      <Input data-testid="combo-mrp-price" type="number" step="5" value={mrpPrice} onChange={(e) => { const p=e.target.value; setMrpPrice(p); const wsp=parseFloat(wspPrice); if(p&&wsp>0){const pr=parseFloat(p);if(pr>0){setMrpMarkup((((pr-wsp)/wsp)*100).toFixed(2));setMrpMarkdown((((pr-wsp)/pr)*100).toFixed(2));}}else{setMrpMarkup("");setMrpMarkdown("");}}} className="h-9 mt-1 font-semibold" disabled={!wspPrice} />
                    </div>
                  </div>
                </div>
                <Separator />
                <div>
                  <Label className="text-sm font-medium text-stone-700 mb-2 block">Calculate WSP from MRP</Label>
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <div><span className="text-xs text-stone-400">MRP</span><Input type="number" step="1" value={revMrp} onChange={(e) => {setRevMrp(e.target.value);setRevWspResult("");setRevMu("");setRevMd("");}} className="h-9 mt-1" /></div>
                    <div><span className="text-xs text-stone-400">Markup %</span><Input type="number" step="0.01" value={revMu} onChange={(e) => {const v=e.target.value;setRevMu(v);setRevMd("");const mrp=parseFloat(revMrp);if(v&&mrp>0){const w=Math.round(mrp/(1+parseFloat(v)/100));setRevWspResult(w);setRevMd((((mrp-w)/mrp)*100).toFixed(2));}else{setRevWspResult("");}}} className="h-9 mt-1" disabled={!revMrp} /></div>
                    <div><span className="text-xs text-stone-400">Markdown %</span><Input type="number" step="0.01" value={revMd} onChange={(e) => {const v=e.target.value;setRevMd(v);setRevMu("");const mrp=parseFloat(revMrp);if(v&&mrp>0){const w=Math.round(mrp*(1-parseFloat(v)/100));setRevWspResult(w);setRevMu((((mrp-w)/w)*100).toFixed(2));}else{setRevWspResult("");}}} className="h-9 mt-1" disabled={!revMrp} /></div>
                  </div>
                  {revWspResult && (<div className="bg-stone-50 rounded-md p-3 text-center"><span className="text-xs text-stone-500">Calculated WSP: </span><span className="font-heading font-bold text-lg text-[#C2410C]">{revWspResult}</span></div>)}
                </div>
              </div>
            </div>

            {/* Save */}
            {/* Save + Export */}
            <div className="flex gap-3">
              <Button data-testid="combo-save-button" className="flex-1 bg-[#C2410C] hover:bg-[#9A3412] text-white h-11" onClick={() => setSaveOpen(true)}>
                <Save className="h-4 w-4 mr-2" /> {loadedDesignId ? "Update Combo Design" : "Save Combo Design"}
              </Button>
              <Button data-testid="combo-export-pdf" variant="outline" className="h-11 border-[#C2410C] text-[#C2410C] hover:bg-orange-50" onClick={exportComboPdf}>
                <FileDown className="h-4 w-4 mr-2" /> Export PDF
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Save Dialog */}
      <Dialog open={saveOpen} onOpenChange={(o) => { if (!o) { setTagStepId(null); setPdfStep(false); } setSaveOpen(o); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{pdfStep ? "Export PDF?" : tagStepId ? "Tag Barcode (Optional)" : loadedDesignId ? "Update Combo Design" : "Save Combo Design"}</DialogTitle></DialogHeader>
          {pdfStep ? (
            <PdfPromptStep
              onExport={() => {
                setPdfStep(false); setSaveOpen(false);
                setTimeout(() => {
                  document.querySelector('[data-testid="combo-export-pdf"]')?.click();
                  setTimeout(() => resetCombo(), 300);
                }, 60);
              }}
              onSkip={() => { setPdfStep(false); setSaveOpen(false); resetCombo(); }}
            />
          ) : tagStepId ? (
            <TagBarcodePicker
              designId={tagStepId}
              onTagged={async () => { const id = tagStepId; setTagStepId(null); await refreshAfterTag(id); setPdfStep(true); }}
              onSkip={() => { setTagStepId(null); setPdfStep(true); }}
            />
          ) : (
            <>
              <div className="space-y-3">
                <div><Label className="text-xs mb-1 block">Design Name</Label><Input data-testid="combo-design-name" value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="e.g. Dhoti + Shirt Combo" /></div>
                <div><Label className="text-xs mb-1 block">Description</Label><Input value={saveDesc} onChange={(e) => setSaveDesc(e.target.value)} placeholder="Optional" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
                <Button data-testid="combo-save-confirm" className="bg-[#C2410C] hover:bg-[#9A3412] text-white" disabled={!saveName.trim() || saving} onClick={saveCombo}>
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} {loadedDesignId ? "Update" : "Save"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
